import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiBlueprint,
  discoveryApiRef,
  fetchApiRef,
  FrontendPlugin,
} from '@backstage/frontend-plugin-api';
import {
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import githubActionsPlugin from '@backstage-community/plugin-github-actions/alpha';
import {
  githubActionsApiRef,
  GithubActionsApi,
  isGithubActionsAvailable,
  GITHUB_ACTIONS_ANNOTATION,
} from '@backstage-community/plugin-github-actions';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { useEntity } from '@backstage/plugin-catalog-react';
import { isExternalUserSync } from '../externalUser';
import {
  EmptyState,
  Link,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import RetryIcon from '@material-ui/icons/Replay';
import LaunchIcon from '@material-ui/icons/Launch';
import CallSplitIcon from '@material-ui/icons/CallSplit';

const GITHUB_ORG = 'savrcom';

/**
 * GithubActionsApi that goes through the backend's /proxy/github/savrcom
 * endpoint (see proxy config in app-config.yaml), which injects the backend's
 * GITHUB_TOKEN. The stock client asks each user to sign in to GitHub via an
 * OAuth popup; internal users sign in with Google and should never need a
 * GitHub account to view CI runs. The proxy is GET-only, so re-running a
 * workflow is not available from the portal.
 */
class ProxiedGithubActionsApi implements GithubActionsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async request(owner: string, path: string, raw?: boolean) {
    if (owner !== GITHUB_ORG) {
      throw new Error(
        `GitHub Actions are only available for ${GITHUB_ORG} repositories (got ${owner})`,
      );
    }
    const base = await this.discoveryApi.getBaseUrl('proxy');
    const response = await this.fetchApi.fetch(`${base}/github/savrcom${path}`);
    if (!response.ok) {
      throw new Error(`GitHub request failed: ${response.status} ${path}`);
    }
    return raw ? response.text() : response.json();
  }

  async listWorkflowRuns(options: {
    owner: string;
    repo: string;
    pageSize?: number;
    page?: number;
    branch?: string;
  }) {
    const { owner, repo, pageSize = 100, page = 0, branch } = options;
    const params = new URLSearchParams({
      per_page: String(pageSize),
      page: String(page),
      ...(branch ? { branch } : {}),
    });
    return this.request(owner, `/${repo}/actions/runs?${params}`);
  }

  async getWorkflow(options: { owner: string; repo: string; id: number }) {
    return this.request(
      options.owner,
      `/${options.repo}/actions/workflows/${options.id}`,
    );
  }

  async getWorkflowRun(options: { owner: string; repo: string; id: number }) {
    return this.request(
      options.owner,
      `/${options.repo}/actions/runs/${options.id}`,
    );
  }

  async reRunWorkflow(): Promise<never> {
    throw new Error(
      'Re-running workflows from the portal is disabled - use GitHub.',
    );
  }

  async listJobsForWorkflowRun(options: {
    owner: string;
    repo: string;
    id: number;
    pageSize?: number;
    page?: number;
  }) {
    const { owner, repo, id, pageSize = 100, page = 0 } = options;
    const params = new URLSearchParams({
      per_page: String(pageSize),
      page: String(page),
    });
    return this.request(owner, `/${repo}/actions/runs/${id}/jobs?${params}`);
  }

  async downloadJobLogsForWorkflowRun(options: {
    owner: string;
    repo: string;
    runId: number;
  }) {
    // The plugin passes the job id as runId; GitHub redirects to a signed
    // log URL, which fetch follows transparently.
    return this.request(
      options.owner,
      `/${options.repo}/actions/jobs/${options.runId}/logs`,
      true,
    );
  }

  async listBranches(options: { owner: string; repo: string; page: number }) {
    const params = new URLSearchParams({
      per_page: '100',
      page: String(options.page ?? 0),
    });
    return this.request(options.owner, `/${options.repo}/branches?${params}`);
  }

  async getDefaultBranch(options: { owner: string; repo: string }) {
    const repo = await this.request(options.owner, `/${options.repo}`);
    return repo.default_branch;
  }
}

// Replaces the plugin's stock api extension (same apiRef -> same extension id).
const githubActionsProxyApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams({
      api: githubActionsApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new ProxiedGithubActionsApi(discoveryApi, fetchApi),
    }),
});

// ---------------------------------------------------------------------------
// Custom GitHub Actions tab, replacing the plugin's stock table with a view
// styled like the rest of the portal: SAVR-palette status pills, branch/SHA
// chips, durations and deep links into GitHub.
// ---------------------------------------------------------------------------

type WorkflowRun = {
  id: number;
  name: string | null;
  display_title?: string;
  head_branch: string | null;
  head_sha: string;
  event: string;
  status: string | null;
  conclusion: string | null;
  html_url: string;
  created_at: string;
  updated_at: string;
  run_started_at?: string;
  actor?: { login?: string };
};

const useStyles = makeStyles(theme => ({
  '@keyframes statusPulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.35 },
    '100%': { opacity: 1 },
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  pulsing: {
    animation: '$statusPulse 1.4s ease-in-out infinite',
  },
  workflowName: {
    fontWeight: 600,
  },
  commitTitle: {
    display: 'block',
    maxWidth: 420,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 500,
  },
  metaRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  branchPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    borderRadius: 999,
    padding: '1px 8px',
    fontSize: 11,
    fontFamily: 'monospace',
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(143, 147, 255, 0.12)'
        : 'rgba(78, 76, 205, 0.08)',
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(143, 147, 255, 0.25)'
        : 'rgba(78, 76, 205, 0.2)'
    }`,
    maxWidth: 260,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sha: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  event: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  timeCell: {
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
}));

// Status colors tuned for the SAVR palette (brand #8F93FF/#4E4CCD, error
// #FF385F/#E11D48) in both themes.
const statusStyle = (run: WorkflowRun, dark: boolean) => {
  const key =
    run.status === 'completed' ? run.conclusion ?? 'unknown' : run.status;
  const make = (color: string, label: string, pulse = false) => ({
    color,
    label,
    pulse,
    background: `${color}${dark ? '1f' : '14'}`,
    border: `${color}4d`,
  });
  switch (key) {
    case 'success':
      return make(dark ? '#34D399' : '#059669', 'Success');
    case 'failure':
    case 'startup_failure':
      return make(dark ? '#FF385F' : '#E11D48', 'Failed');
    case 'timed_out':
      return make(dark ? '#FF385F' : '#E11D48', 'Timed out');
    case 'action_required':
      return make(dark ? '#F5A623' : '#D97706', 'Action required');
    case 'in_progress':
      return make(dark ? '#8F93FF' : '#4E4CCD', 'Running', true);
    case 'queued':
    case 'waiting':
    case 'pending':
    case 'requested':
      return make(dark ? '#F5A623' : '#D97706', 'Queued', true);
    case 'cancelled':
      return make(dark ? '#8B8B93' : '#6B7280', 'Cancelled');
    case 'skipped':
    case 'neutral':
      return make(dark ? '#8B8B93' : '#6B7280', 'Skipped');
    default:
      return make(dark ? '#8B8B93' : '#6B7280', key ?? 'Unknown');
  }
};

const formatAge = (iso: string) => {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 30) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const formatDuration = (run: WorkflowRun) => {
  if (run.status !== 'completed' || !run.run_started_at) {
    return '';
  }
  const seconds = Math.max(
    0,
    (new Date(run.updated_at).getTime() -
      new Date(run.run_started_at).getTime()) /
      1000,
  );
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

function StatusPill({ run }: { run: WorkflowRun }) {
  const classes = useStyles();
  const theme = useTheme();
  const s = statusStyle(run, theme.palette.type === 'dark');
  return (
    <span
      className={classes.statusPill}
      style={{
        color: s.color,
        backgroundColor: s.background,
        border: `1px solid ${s.border}`,
      }}
    >
      <span
        className={`${classes.statusDot} ${s.pulse ? classes.pulsing : ''}`}
        style={{ backgroundColor: s.color }}
      />
      {s.label}
    </span>
  );
}

function SavrGithubActionsContent() {
  const classes = useStyles();
  const { entity } = useEntity();
  const api = useApi(githubActionsApiRef);

  const slug = entity.metadata.annotations?.[GITHUB_ACTIONS_ANNOTATION] ?? '/';
  const [owner, repo] = slug.split('/');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    api
      .listWorkflowRuns({ owner, repo, pageSize, page: page + 1 })
      .then(data => {
        if (!cancelled) {
          setRuns((data.workflow_runs ?? []) as WorkflowRun[]);
          // GitHub reports up to 1000 runs as pageable through this endpoint.
          setTotal(Math.min(data.total_count ?? 0, 1000));
          setLoading(false);
        }
      })
      .catch(e => {
        if (!cancelled) {
          setError(e);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, owner, repo, page, pageSize, reloadKey]);

  const columns: TableColumn<WorkflowRun>[] = useMemo(
    () => [
      {
        title: 'Status',
        width: '130px',
        render: run => <StatusPill run={run} />,
      },
      {
        title: 'Workflow',
        width: '210px',
        render: run => (
          <>
            <Typography variant="body2" className={classes.workflowName}>
              {run.name ?? 'Workflow'}
            </Typography>
            <span className={classes.event}>{run.event}</span>
          </>
        ),
      },
      {
        title: 'Commit',
        render: run => (
          <>
            <Link to={run.html_url} className={classes.commitTitle}>
              {run.display_title ?? run.head_sha.slice(0, 7)}
            </Link>
            <span className={classes.metaRow}>
              <span className={classes.branchPill}>
                <CallSplitIcon style={{ fontSize: 11 }} />
                {run.head_branch ?? 'detached'}
              </span>
              <span className={classes.sha}>{run.head_sha.slice(0, 7)}</span>
              {run.actor?.login && (
                <span className={classes.event}>by {run.actor.login}</span>
              )}
            </span>
          </>
        ),
      },
      {
        title: 'Duration',
        width: '110px',
        render: run => (
          <span className={classes.timeCell}>{formatDuration(run)}</span>
        ),
      },
      {
        title: 'Age',
        width: '110px',
        render: run => (
          <Tooltip title={new Date(run.created_at).toLocaleString()}>
            <span className={classes.timeCell}>
              {formatAge(run.created_at)}
            </span>
          </Tooltip>
        ),
      },
    ],
    [classes],
  );

  const refresh = useCallback(() => setReloadKey(k => k + 1), []);

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }
  if (!loading && total === 0 && page === 0) {
    return (
      <EmptyState
        missing="data"
        title="No workflow runs"
        description={`This repository (${slug}) has GitHub Actions enabled, but no runs yet.`}
      />
    );
  }

  return (
    <Table<WorkflowRun>
      title={`${owner}/${repo}`}
      subtitle={`${total.toLocaleString()}${
        total >= 1000 ? '+' : ''
      } workflow runs`}
      columns={columns}
      data={runs}
      isLoading={loading}
      page={page}
      totalCount={total}
      onPageChange={setPage}
      onRowsPerPageChange={size => {
        setPageSize(size);
        setPage(0);
      }}
      actions={[
        {
          icon: () => <RetryIcon />,
          tooltip: 'Refresh',
          isFreeAction: true,
          onClick: refresh,
        },
        run => ({
          icon: () => <LaunchIcon fontSize="small" />,
          tooltip: 'Open run in GitHub',
          onClick: () =>
            window.open((run as WorkflowRun).html_url, '_blank', 'noopener'),
        }),
      ]}
      options={{
        search: false,
        paging: true,
        pageSize,
        pageSizeOptions: [10, 20, 50],
        sorting: false,
        actionsColumnIndex: -1,
        padding: 'dense',
        emptyRowsWhenPaging: false,
      }}
    />
  );
}

// Replaces the plugin's stock entity tab (same unnamed extension id).
// Hidden for external users unless the entity opts its repo in - the real
// enforcement is the backend's githubProxyExternalGuard; this only avoids
// showing a tab that would 403.
const savrGithubActionsContent = EntityContentBlueprint.make({
  params: {
    path: 'github-actions',
    title: 'GitHub Actions',
    filter: entity =>
      isGithubActionsAvailable(entity) &&
      (!isExternalUserSync() ||
        entity.metadata.annotations?.['savr.com/external-actions'] === 'true'),
    loader: async () => <SavrGithubActionsContent />,
  },
});

export const githubActionsPluginWithOverrides: FrontendPlugin =
  githubActionsPlugin.withOverrides({
    extensions: [githubActionsProxyApi, savrGithubActionsContent],
  });
